import React, { useState, useEffect, useRef } from 'react';
import Script from 'next/script';
import Image from 'next/image';

declare global {
    interface Window {
        Vimeo: any;
    }
}

interface VimeoPlayerProps {
    videoId: string;
    posterImage: any;
    isVisible: boolean;
    className?: string;
    autoplay?: boolean;
    muted?: boolean;
    loop?: boolean;
    controls?: boolean;
    aspectRatio?: '16:9' | '4:3' | '1:1' | '9:16';
    pauseWhenHidden?: boolean;
    resumeWhenVisible?: boolean;
}

const VimeoPlayer: React.FC<VimeoPlayerProps> = ({
    videoId,
    posterImage,
    isVisible,
    className = '',
    autoplay = true,
    muted = true,
    loop = false,
    controls = true,
    aspectRatio = '16:9',
    pauseWhenHidden = true,
    resumeWhenVisible = false
}) => {
    const [isPlayerLoaded, setIsPlayerLoaded] = useState(false);
    const [shouldLoadPlayer, setShouldLoadPlayer] = useState(false);
    const [wasPlayingBeforeHidden, setWasPlayingBeforeHidden] = useState(false);
    const playerRef = useRef<any>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const hasPlayedRef = useRef(false);

    // Calculate padding based on aspect ratio
    const aspectRatioPadding = {
        '16:9': '56.25%',
        '4:3': '75%',
        '1:1': '100%',
        '9:16': '177.78%'
    };

    // Load player when element becomes visible
    useEffect(() => {
        if (isVisible && !shouldLoadPlayer) {
            setShouldLoadPlayer(true);
        }
    }, [isVisible, shouldLoadPlayer]);

    // Initialize Vimeo player
    useEffect(() => {
        if (!shouldLoadPlayer || !window.Vimeo || playerRef.current) return;

        const initializePlayer = () => {
            if (!containerRef.current) return;

            try {
                playerRef.current = new window.Vimeo.Player(containerRef.current, {
                    id: videoId,
                    width: '100%',
                    autoplay: false, // We'll control autoplay manually
                    muted: muted,
                    loop: loop,
                    controls: controls,
                    responsive: true,
                    playsinline: true,
                    background: false,
                    quality: 'auto'
                });

                playerRef.current.on('loaded', () => {
                    setIsPlayerLoaded(true);
                });

                playerRef.current.on('error', (error: any) => {
                    console.error('Vimeo player error:', error);
                });
            } catch (error) {
                console.error('Error initializing Vimeo player:', error);
            }
        };

        // Small delay to ensure DOM is ready
        const timer = setTimeout(initializePlayer, 100);
        return () => clearTimeout(timer);
    }, [shouldLoadPlayer, videoId, muted, loop, controls]);

    // Handle autoplay when first becoming visible
    useEffect(() => {
        if (isVisible && isPlayerLoaded && autoplay && !hasPlayedRef.current && playerRef.current) {
            playerRef.current.play().catch((error: any) => {
                console.log('Autoplay prevented:', error);
            });
            hasPlayedRef.current = true;
        }
    }, [isVisible, isPlayerLoaded, autoplay]);

    // Handle pause/resume based on visibility changes
    useEffect(() => {
        if (!isPlayerLoaded || !playerRef.current || !pauseWhenHidden || !hasPlayedRef.current) return;

        const handleVisibilityChange = async () => {
            try {
                if (!isVisible) {
                    // Going out of view - check if playing and pause
                    const paused = await playerRef.current.getPaused();
                    if (!paused) {
                        setWasPlayingBeforeHidden(true);
                        await playerRef.current.pause();
                    }
                } else if (resumeWhenVisible && wasPlayingBeforeHidden) {
                    // Coming back into view - resume if it was playing
                    await playerRef.current.play();
                    setWasPlayingBeforeHidden(false);
                }
            } catch (error) {
                console.log('Error handling visibility change:', error);
            }
        };

        handleVisibilityChange();
    }, [isVisible, isPlayerLoaded, pauseWhenHidden, resumeWhenVisible]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (playerRef.current) {
                playerRef.current.destroy();
            }
        };
    }, []);

    return (
        <>
            {shouldLoadPlayer && <Script src='https://player.vimeo.com/api/player.js' strategy='lazyOnload' />}

            <div className={`relative overflow-hidden ${className}`} style={{ paddingBottom: aspectRatioPadding[aspectRatio] }}>
                {/* Poster Image */}
                <div
                    className={`absolute inset-0 w-full h-full transition-opacity duration-300 ${
                        isPlayerLoaded && shouldLoadPlayer ? 'opacity-0 pointer-events-none' : 'opacity-100'
                    }`}
                >
                    <Image src={posterImage} alt='Video poster' className='w-full h-full object-cover' loading='lazy' />
                    {/* Optional loading spinner */}
                    {shouldLoadPlayer && !isPlayerLoaded && (
                        <div className='absolute inset-0 flex items-center justify-center'>
                            <div className='w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin'></div>
                        </div>
                    )}
                </div>

                {/* Vimeo Player Container */}
                {shouldLoadPlayer && (
                    <div
                        ref={containerRef}
                        className={`absolute inset-0 w-full h-full transition-opacity duration-300 ${isPlayerLoaded ? 'opacity-100' : 'opacity-0'}`}
                    />
                )}
            </div>
        </>
    );
};

export default VimeoPlayer;
